import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useMemo } from "react";
import { buildKardaMuiTheme } from "./theme/buildKardaMuiTheme";
import { ToastProvider } from "./shared/toast";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";

import LoginPage from "./pages/Login";
import ProfilePage from "./pages/Profile";
import HomePage from "./pages/Home";
import VideoSelectionPage from "./pages/VideoSelection";
import GenerateReportPage from "./pages/GenerateReport";
import EditSectionPage from "./pages/EditSection";
import ConfirmationPage from "./pages/Confirmation";
import NotFound404 from "./pages/NotFound";
import { ExtractedInfoPage } from "./pages/ExtractedInfo";
import { FinalEditsPage } from "./pages/FinalEdits";
import ReviewPlanPage from "./pages/ReviewPlan";
import { ReviewPlanRouteGuard } from "./pages/ReviewPlan/ReviewPlanRouteGuard";
import ManageUsersPage from "./pages/Admin/ManageUsers";
import { ProtectedView } from "./components/layout/ProtectedView";
import { ConnectBodycam } from "./pages/ConnectBodycam";
import { AnalyticsPage } from "./pages/Analytics";
import { AuthTokenBridge } from "./components/auth/AuthTokenBridge";
import { AuthCallback } from "./pages/AuthCallback";
import { InitiateLogin } from "./pages/InitiateLogin";
import { RequirePermission } from "./components/auth/RequirePermission";
import { RequireCoApproval } from "./components/auth/RequireCoApproval";
import {
  USERS_ADMIN_PERMISSION,
  REPORTS_MANAGE_STATION_PERMISSION,
} from "./shared/auth/authorization";
import CoReviewQueuePage from "./pages/CoReview/CoReviewQueuePage";
import CoReviewPage from "./pages/CoReview/CoReviewPage";
import { ColorModeProvider, useColorMode } from "./context/ColorModeContext";
import { AuthContextProvider } from "./context/AuthContext";
import { RequireRole } from "./components/auth/RequireRole";
import {
  KardaAdminPage,
  KardaStationDetailPage,
  StationDetailsSection,
  StationUsersSection,
  StationBodycamSection,
  StationSettingsSection,
  KardaAdminProvider,
} from "./pages/KardaAdmin";
import KardaAdminLayout from "./pages/KardaAdmin/KardaAdminLayout";
import TemplateUploadPage from "./pages/TemplateUpload/TemplateUploadPage";

function AppContent() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildKardaMuiTheme(mode), [mode]);

  const protectedPages = (
    <>
      <Route path="/" element={<HomePage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/video-selection" element={<VideoSelectionPage />} />
      <Route
        path="/generate-report/:evidenceID"
        element={<GenerateReportPage />}
      />
      <Route
        path="/preview-report-info/:reportID"
        element={<ExtractedInfoPage />}
      />
      <Route path="/edit-section/:reportID" element={<EditSectionPage />} />
      <Route
        path="/review-plan/:reportID?"
        element={
          <ReviewPlanRouteGuard>
            <ReviewPlanPage />
          </ReviewPlanRouteGuard>
        }
      />
      <Route path="/final-edits/:reportID" element={<FinalEditsPage />} />
      <Route
        path="/confirmation/:eventNumber?"
        element={<ConfirmationPage />}
      />
      <Route
        path="/co-review"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewQueuePage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/co-review/:reportID"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewPage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/manage-users"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ManageUsersPage />
          </RequirePermission>
        }
      />
      <Route
        path="/karda-admin"
        element={
          <RequireRole roleSlug="org-karda-admin">
            <KardaAdminProvider />
          </RequireRole>
        }
      >
        <Route element={<KardaAdminLayout />}>
          <Route index element={<KardaAdminPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="templates" element={<TemplateUploadPage />} />
          <Route
            path="stations/:stationId"
            element={<KardaStationDetailPage />}
          >
            <Route index element={<StationDetailsSection />} />
            <Route path="users" element={<StationUsersSection />} />
            <Route path="bodycam" element={<StationBodycamSection />} />
            <Route path="settings" element={<StationSettingsSection />} />
          </Route>
        </Route>
      </Route>
      <Route
        path="/connect-bodycam"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ConnectBodycam />
          </RequirePermission>
        }
      />
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <Router>
        <AuthTokenBridge />
        <Routes>
          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <LoginPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/initiate-login"
            element={
              <RouteErrorBoundary>
                <InitiateLogin />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/callback"
            element={
              <RouteErrorBoundary>
                <AuthCallback />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedView>
                <RouteErrorBoundary>
                  <Outlet />
                </RouteErrorBoundary>
              </ProtectedView>
            }
          >
            {protectedPages}
          </Route>
          <Route
            path="*"
            element={
              <RouteErrorBoundary>
                <NotFound404 />
              </RouteErrorBoundary>
            }
          />
        </Routes>
      </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary variant="bare">
      <ColorModeProvider>
        <AuthContextProvider>
          <AppContent />
        </AuthContextProvider>
      </ColorModeProvider>
    </ErrorBoundary>
  );
}

export default App;
